import * as React from 'react';

function isUrlOrPath(source) {
  return (
    typeof source === 'string' &&
    (source.startsWith('/') ||
      source.startsWith('./') ||
      source.startsWith('http://') ||
      source.startsWith('https://'))
  );
}

export function getWebImageVariantUrl(source) {
  const original = String(source || '').trim();
  if (!original) return '';

  const match = original.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/);
  const path = match?.[1] || original;
  const query = match?.[2] || '';
  const hash = match?.[3] || '';

  if (/_web\.webp$/i.test(path)) return original;
  if (!/\.(png|jpe?g|webp|gif|bmp|tiff?)$/i.test(path)) return original;

  return (
    path.replace(/\.(png|jpe?g|webp|gif|bmp|tiff?)$/i, '_web.webp') +
    query +
    hash
  );
}

export function usePreloadedOptimizedConfigImageSrc(source, retries = 0) {
  const originalSource = typeof source === 'string' ? source.trim() : '';
  const retryCount = Math.max(0, Number(retries) || 0);
  const [state, setState] = React.useState({
    ready: false,
    resolvedSrc: '',
  });

  React.useEffect(() => {
    let canceled = false;
    let retryTimer = null;
    let probe = null;

    setState({ ready: false, resolvedSrc: '' });

    if (!originalSource) {
      setState({ ready: true, resolvedSrc: '' });
      return () => {
        canceled = true;
      };
    }

    if (!isUrlOrPath(originalSource)) {
      setState({ ready: true, resolvedSrc: originalSource });
      return () => {
        canceled = true;
      };
    }

    const optimizedSource = getWebImageVariantUrl(originalSource);
    const candidates =
      optimizedSource && optimizedSource !== originalSource
        ? [optimizedSource, originalSource]
        : [originalSource];

    const tryLoad = (candidateIndex, attempt = 0) => {
      const candidate = candidates[candidateIndex];
      probe = new window.Image();
      probe.onload = () => {
        if (!canceled) {
          setState({ ready: true, resolvedSrc: candidate });
        }
      };
      probe.onerror = () => {
        if (canceled) return;

        if (candidateIndex < candidates.length - 1) {
          tryLoad(candidateIndex + 1);
          return;
        }

        if (attempt < retryCount) {
          retryTimer = window.setTimeout(
            () => tryLoad(candidateIndex, attempt + 1),
            300 * Math.pow(2, attempt)
          );
          return;
        }

        setState({ ready: true, resolvedSrc: '' });
      };
      probe.src = candidate;
    };

    tryLoad(0);

    return () => {
      canceled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      if (probe) {
        probe.onload = null;
        probe.onerror = null;
      }
    };
  }, [originalSource, retryCount]);

  return state;
}

const OptimizedConfigImage = React.forwardRef(function OptimizedConfigImage(
  { src, onError, ...props },
  ref
) {
  const originalSource = String(src || '').trim();
  const optimizedSource = getWebImageVariantUrl(originalSource);
  const canTryOptimized =
    Boolean(originalSource) && optimizedSource !== originalSource;
  const [useOriginal, setUseOriginal] = React.useState(!canTryOptimized);

  React.useEffect(() => {
    setUseOriginal(!canTryOptimized);
  }, [canTryOptimized, optimizedSource, originalSource]);

  return (
    <img
      {...props}
      ref={ref}
      src={useOriginal ? originalSource : optimizedSource}
      onError={(event) => {
        if (!useOriginal && originalSource) {
          setUseOriginal(true);
          return;
        }
        onError?.(event);
      }}
    />
  );
});

export default OptimizedConfigImage;
